// ═══════════════════════════════════════════════════════════
// Default Role Definitions
// ═══════════════════════════════════════════════════════════

import type { RbacPermissions } from './rbac-types';

function allModules(level: 'none' | 'viewer' | 'editor' | 'admin'): RbacPermissions {
  return {
    hotwash: level, handoff: level, rollcall: level, geoops: level,
    impact: level, intake: level, battlerhythm: level, pace: level,
    volunteer: level, mainstreet: level, filelibrary: level, blackboard: level,
    incidents: level, debris: level, shelter: level, pagrants: level,
    timelabor: level, workflows: level, forms: level,
  };
}

export interface DefaultRole {
  roleSlug: string;
  roleName: string;
  description: string;
  permissions: RbacPermissions;
  isDefault: boolean;
  sortOrder: number;
}

export const DEFAULT_ROLES: DefaultRole[] = [
  {
    roleSlug: 'super_admin', roleName: 'Super Admin', description: 'Full access to all modules',
    permissions: allModules('admin'), isDefault: false, sortOrder: 1,
  },
  {
    roleSlug: 'eoc_director', roleName: 'EOC Director', description: 'Admin access across all modules',
    permissions: { ...allModules('admin'), forms: 'editor' }, isDefault: false, sortOrder: 2,
  },
  {
    roleSlug: 'operations_staff', roleName: 'Operations Staff', description: 'Editor on operational modules',
    permissions: {
      ...allModules('viewer'),
      hotwash: 'editor', handoff: 'editor', rollcall: 'editor', geoops: 'editor',
      impact: 'editor', intake: 'editor', battlerhythm: 'editor', incidents: 'editor',
    }, isDefault: true, sortOrder: 3,
  },
  {
    roleSlug: 'field_staff', roleName: 'Field Staff', description: 'Field data entry',
    permissions: {
      ...allModules('none'),
      hotwash: 'editor', rollcall: 'editor', impact: 'editor',
      pace: 'viewer', incidents: 'viewer',
    }, isDefault: false, sortOrder: 4,
  },
  {
    roleSlug: 'viewer', roleName: 'Viewer', description: 'Read-only access to all modules',
    permissions: allModules('viewer'), isDefault: false, sortOrder: 5,
  },
  {
    roleSlug: 'debris_ops_mgr', roleName: 'Debris Operations Manager', description: 'Admin on debris operations',
    permissions: { ...allModules('none'), debris: 'admin', impact: 'viewer', incidents: 'viewer' },
    isDefault: false, sortOrder: 10,
  },
  {
    roleSlug: 'shelter_mgr', roleName: 'Shelter Manager', description: 'Admin on shelter operations',
    permissions: {
      ...allModules('none'), shelter: 'admin', impact: 'viewer',
      intake: 'viewer', volunteer: 'viewer', incidents: 'viewer',
    }, isDefault: false, sortOrder: 11,
  },
  {
    roleSlug: 'pa_admin', roleName: 'PA Administrator', description: 'Admin on disaster grants and time & labor',
    permissions: {
      ...allModules('none'), pagrants: 'admin', timelabor: 'admin',
      workflows: 'editor', impact: 'viewer', incidents: 'viewer', filelibrary: 'viewer',
    }, isDefault: false, sortOrder: 12,
  },
  {
    roleSlug: 'pa_applicant_mgr', roleName: 'PA Applicant Manager', description: 'Applicant organization manager',
    permissions: { ...allModules('none'), pagrants: 'editor', timelabor: 'editor', incidents: 'viewer' },
    isDefault: false, sortOrder: 13,
  },
  {
    roleSlug: 'pa_applicant_staff', roleName: 'PA Applicant Staff', description: 'Applicant organization staff',
    permissions: { ...allModules('none'), pagrants: 'editor', incidents: 'viewer' },
    isDefault: false, sortOrder: 14,
  },
  {
    roleSlug: 'pa_applicant_viewer', roleName: 'PA Applicant Viewer', description: 'Applicant read-only',
    permissions: { ...allModules('none'), pagrants: 'viewer', incidents: 'viewer' },
    isDefault: false, sortOrder: 15,
  },
  {
    roleSlug: 'volunteer_coord', roleName: 'Volunteer Coordinator', description: 'Admin on volunteer management',
    permissions: { ...allModules('none'), volunteer: 'admin', rollcall: 'editor', incidents: 'viewer' },
    isDefault: false, sortOrder: 16,
  },
  {
    roleSlug: 'biz_owner', roleName: 'Business Owner', description: 'MainStreet business portal access',
    permissions: { ...allModules('none'), mainstreet: 'viewer' },
    isDefault: false, sortOrder: 17,
  },
  {
    roleSlug: 'handoff_only', roleName: 'Handoff Only', description: 'Shift handoff personnel',
    permissions: { ...allModules('none'), handoff: 'editor' },
    isDefault: false, sortOrder: 18,
  },
  {
    roleSlug: 'timelabor_only', roleName: 'Time Entry Only', description: 'Basic time entry user',
    permissions: { ...allModules('none'), timelabor: 'editor' },
    isDefault: false, sortOrder: 19,
  },
  {
    roleSlug: 'bb_student', roleName: 'BB Student', description: 'Blackboard training student',
    permissions: { ...allModules('none'), blackboard: 'viewer' },
    isDefault: false, sortOrder: 20,
  },
];
